// Best-effort IP-based language default for first-time visitors.
// Only runs when the visitor has no saved language preference yet — an
// explicit choice (via the language switcher, cached by i18next-browser-
// languagedetector under localStorage key "hayat-lang") is never overridden.
// English is the default when the country is unknown or has no enabled language.

const GERMAN_COUNTRIES = new Set(["DE", "AT", "CH", "LI"]);
const TURKISH_COUNTRIES = new Set(["TR"]);

// Note: "hayat-lang" (managed by i18next-browser-languagedetector) is NOT a
// reliable signal on its own — the detector writes the resolved language
// (even just the fallback) to that key as soon as init runs, whether or not
// the visitor ever made a choice. EXPLICIT_KEY is set only by the language
// switcher itself, so it's the only trustworthy "the visitor picked this" flag.
export const EXPLICIT_KEY = "hayat-lang-explicit";
const FETCH_TIMEOUT_MS = 3500;

const languageForCountry = (code) => {
  if (!code) return null;
  if (GERMAN_COUNTRIES.has(code)) return "de";
  if (TURKISH_COUNTRIES.has(code)) return "tr";
  return "en";
};

export const applyGeoLanguageDefault = async (i18n) => {
  try {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(EXPLICIT_KEY)) return; // explicit/cached choice wins

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    let lang = "en";
    try {
      const res = await fetch("https://ipwho.is/", { signal: controller.signal });
      if (res.ok) {
        const data = await res.json();
        if (data && data.success && data.country_code) {
          lang = languageForCountry(data.country_code) || "en";
        }
      }
    } finally {
      clearTimeout(timer);
    }

    // A user could have picked a language manually while the lookup was in flight.
    if (window.localStorage.getItem(EXPLICIT_KEY)) return;
    if (i18n.language !== lang) await i18n.changeLanguage(lang);
  } catch {
    // Network/geo-IP failure: fall back to English, unless
    // the visitor already made an explicit choice in the meantime.
    try {
      if (!window.localStorage.getItem(EXPLICIT_KEY) && i18n.language !== "en") {
        await i18n.changeLanguage("en");
      }
    } catch {
      // Best-effort only — never block app startup on this.
    }
  }
};
